import React, { useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import MaterialTable from '@material-table/core';

import flowRight from 'lodash.flowright';
import { injectIntl } from 'react-intl';

import { mapTableOptions } from '../../../consts/tableOptions';
import useTableLocalization from '../../../utils/useTableLocalization';
import Optional from '../../../utils/optional';
import CustomTableTitle from '../../../partials/CustomTableTitle';
import FilterButton from '../../../partials/tableCustomButtons/FilterButton';
import UserDetailView from './UserDetailView';
import tableColumns from './tableColumns';
import {
  doesArrayHasLength,
  isArrayEmpty,
  isDefined
} from '../../../utils/isDefined';
import { actions as requestUserWizardReduxActions } from '../../../modals/requestUserWizard/reducers';
import AddButton from '../../../partials/tableCustomButtons/AddButton';
import useLocalTableStore from '../../../utils/useLocalTableStore';
import { actions as reduxActions } from '../reducers';
import { lockUserAction } from '../actions/lockUser';
import customTableIcons from '../../../partials/tableIcons/customTableIcons';
import filterCustomJobs from '../../../utils/filterCustomJobs';
import CustomJobsMenu from '../../../partials/customJobsMenu/CustomJobsMenu';
import withSettings from '../../../utils/withSettings';
import can from '../../../utils/can';
import createSubject from '../../../utils/createSubject';
import { getUsers } from '../actions/getUsers';
import RefreshButton from '../../../partials/tableCustomButtons/RefreshButton';
import { actions as requestUserWizardOldReduxActions } from '../../../modals/requestUserWizardOld/reducers';
import withUserCheck from '../../../utils/withUserCheck';
import CustomIconButton from '../../../partials/tableCustomButtons/CustomIconButton';
import { actions as activationMessageDialogReduxActions } from '../../../partials/activationMessageDialog/reducers';

export function UsersTable({
  users,
  intl,
  clientId,
  isLoading,
  openRequestNewUser,
  openRequestNewUserOld,
  setColumns,
  columns,
  lockUserAction,
  dirFields,
  filtersOptions,
  clientName,
  customJobs,
  settings,
  hasClientAccounts,
  isOpening,
  getUsers,
  connectClientId,
  hasClientPartnerId,
  isAdminUser,
  openActivationMessageDialog
}) {
  const [filtering, setFiltering] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { localization, language } = useTableLocalization();

  useEffect(() => {
    getUsers(connectClientId, clientId);
    // eslint-disable-next-line
  }, [connectClientId, clientId]);
  useEffect(() => {
    if (refresh) {
      getUsers(connectClientId, clientId);
      setRefresh(false);
    }
    // eslint-disable-next-line
  }, [refresh]);

  const tableDefaultColumns = useMemo(() => {
    return tableColumns({
      clientId,
      lockUserAction,
      filtersOptions,
      settings,
      users
    });
  }, [clientId, lockUserAction, filtersOptions, settings, users]);

  const {
    pageSize,
    onChangeRowsPerPage,
    onChangeColumnHidden
  } = useLocalTableStore({
    tableName: 'TelephonyUsers-2.1',
    setColumns,
    tableDefaultColumns,
    language
  });

  const mappedJobs = useMemo(() => {
    return filterCustomJobs(customJobs, 'TelephonyUsers');
  }, [customJobs]);

  const actions = useMemo(
    () => {
      let buttons = [
        {
          icon: () => <FilterButton />,
          onClick: () => {
            setFiltering(!filtering);
          },
          isFreeAction: true,
          tooltip: intl.formatMessage({ id: 'BUTTON.FILTER' })
        },
        {
          icon: () => <AddButton buttonText={'BUTTON.REQUEST_USER'} />,
          onClick: () =>
            hasClientPartnerId
              ? openRequestNewUserOld(dirFields)
              : openRequestNewUser(dirFields),
          isFreeAction: true,
          hidden:
            !hasClientAccounts ||
            !can(
              'update',
              createSubject('User', { client_id: Number(clientId) })
            )
              ? true
              : false
        }
      ];

      if (doesArrayHasLength(mappedJobs)) {
        buttons = [
          ...buttons,
          {
            icon: () => <CustomJobsMenu customJobs={mappedJobs} />,
            onClick: () => {},
            isFreeAction: true
          }
        ];
      }

      buttons = [
        ...buttons,
        {
          icon: () => <RefreshButton tableHeader={true} />,
          onClick: () => setRefresh(true),
          isFreeAction: true
        }
      ];

      if (isAdminUser) {
        buttons = [
          ...buttons,
          {
            tooltip: intl.formatMessage({
              id: 'TOOLTIP.ACTIVATION_MESSAGE'
            }),
            icon: () => <CustomIconButton iconClassName="far fa-envelope" />,
            onClick: (evt, data) =>
              openActivationMessageDialog({
                users: data,
                isBulkRequest: true
              })
          }
        ];
      }

      return buttons;
    },
    // eslint-disable-next-line
    [filtering, clientId, dirFields, customJobs]
  );

  const tableOptions = useMemo(() => {
    return {
      ...mapTableOptions({ clientName, tableName: 'telepoUsers' }),
      filtering,
      header: doesArrayHasLength(users),
      pageSize,
      selection: isAdminUser
    };
  }, [filtering, users, clientName, pageSize]);

  const detailPanel = [
    rowData => ({
      disabled: isArrayEmpty(rowData.fields),
      icon: () => (
        <i
          className="fas fa-chevron-right"
          style={{
            display: isArrayEmpty(rowData.fields) ? 'none' : '',
            fontSize: '12px'
          }}
        ></i>
      ),
      openIcon: () => (
        <i
          className="fas fa-chevron-down"
          style={{
            fontSize: '12px'
          }}
        ></i>
      ),
      render: () => <UserDetailView data={rowData} />
    })
  ];

  return (
    <MaterialTable
      title={
        <CustomTableTitle
          text="TABLE.TELEPHONY_USERS"
          icon={<i className="fas fa-user-friends"></i>}
        />
      }
      icons={customTableIcons}
      actions={actions}
      columns={columns}
      data={users}
      options={tableOptions}
      detailPanel={detailPanel}
      localization={localization}
      onChangeColumnHidden={onChangeColumnHidden}
      onRowsPerPageChange={onChangeRowsPerPage}
      isLoading={isLoading || isOpening}
    />
  );
}

const mapStatesToProps = ({ telephonyUsers, auth, selectClient, loader }) => {
  return {
    ...telephonyUsers,
    isLoading: loader.isSpinVisible,
    isOpening: loader.isLinearVisible,
    clientDomain: selectClient.selectedClient?.connect30_domain,
    connectClientId: selectClient.selectedClient?.connect30_domain,
    clientName: selectClient.selectedClient?.name,
    filtersOptions: telephonyUsers.filtersOptions,
    customJobs: auth.customJobs,
    clientId: Optional(selectClient.selectedClient?.id).or(
      auth.defaultClient?.id
    ),
    hasClientPartnerId: isDefined(selectClient.selectedClient?.partner_id),
    hasClientAccounts: Optional(
      selectClient.selectedClient?.metadata?.account_ids
    )
      .map(accounts => doesArrayHasLength(accounts))
      .or(false)
  };
};

const mapDispatchToProps = {
  openRequestNewUser: requestUserWizardReduxActions.openUserWizard,
  openRequestNewUserOld: requestUserWizardOldReduxActions.openUserWizard,
  setColumns: reduxActions.setColumns,
  lockUserAction,
  openActivationMessageDialog: activationMessageDialogReduxActions.openDialog,
  getUsers
};

export default flowRight(
  injectIntl,
  withSettings,
  withUserCheck,
  connect(mapStatesToProps, mapDispatchToProps)
)(UsersTable);
